import React, { type HTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import { FontWeight, Heading, useFontClass } from '../../tokens';
import { LiliRadioInput, type LiliRadioInputProps } from '../LiliRadioInput';

export interface LiliRadioBoxProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  /** Optional icon to display on the left side */
  icon?: React.ReactNode;
  title: string;
  /** Optional slot to render next to the title (e.g., tooltip icon) */
  titleSlot?: React.ReactNode;
  text?: string;
  disabled?: boolean;
  value: string | number;
  checked?: boolean;
  name: string;
  onClick?: (value: string | number) => void;
  id?: string;
  radioButtonOverrideProps?: Partial<LiliRadioInputProps>;
}

export const LiliRadioBox = (props: LiliRadioBoxProps) => {
  const {
    icon,
    title,
    titleSlot,
    text,
    disabled = false,
    value,
    checked = false,
    name,
    onClick,
    className,
    id,
    radioButtonOverrideProps,
    ...divProps
  } = props;

  const { fontClass: fontClassMedium } = useFontClass(FontWeight.MEDIUM);
  const { fontClass } = useFontClass();

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(value);
    }
  };

  const wrapperClassName = twMerge(
    'transition ease-in flex items-center justify-between py-[14px] pl-[14px] pr-[18px] rounded-[12px] border min-h-[56px] border-outline-primary-bw-04 hover:border-outline-brand-primary cursor-pointer group',
    disabled && 'border-none cursor-not-allowed opacity-50',
    checked && !disabled && 'border-outline-brand-primary cursor-pointer',
    className
  );

  return (
    <div id={id} {...divProps} onClick={handleClick} className={wrapperClassName}>
      <div className='flex flex-1 gap-[10px] items-center min-w-0 mr-[12px]'>
        {icon && (
          <div className='shrink-0 w-[24px] h-[24px] flex items-center justify-center text-text-main-primary'>
            {icon}
          </div>
        )}

        <div className='flex flex-col flex-1 min-w-0'>
          <div className='flex gap-[2px] items-center'>
            <Heading.H9 className={`${fontClassMedium} ${disabled && 'text-text-main-tertiary'}`}>
              {title}
            </Heading.H9>
            {titleSlot && <div className='shrink-0'>{titleSlot}</div>}
          </div>

          {text && (
            <Heading.H10
              className={`${fontClass} ${disabled ? 'text-text-main-tertiary' : 'text-text-main-secondary'}`}>
              {text}
            </Heading.H10>
          )}
        </div>
      </div>

      <LiliRadioInput
        id={`${id}-input`}
        name={name}
        checked={checked}
        disabled={disabled}
        onChange={() => handleClick()}
        className='group-hover:border-outline-brand-primary'
        {...radioButtonOverrideProps}
      />
    </div>
  );
};
